import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import api from '../../lib/api';
import {
  HeartPulse, Mail, Plus, X, Send, Save, Clock, Users,
  CheckCircle2, AlertCircle, History, Activity, RefreshCw, Play,
} from 'lucide-react';
import { toast } from 'sonner';

const INTERVALOS = [
  { value: 5,  label: 'Cada 5 minutos' },
  { value: 15, label: 'Cada 15 minutos' },
  { value: 30, label: 'Cada 30 minutos' },
  { value: 60, label: 'Cada 1 hora' },
];

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'medium' });
  } catch { return iso; }
};

function EmailListInput({ label, items, onChange, placeholder, testId = 'emails' }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim().toLowerCase();
    if (!v || items.includes(v)) { setDraft(''); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error('Email inválido');
      return;
    }
    onChange([...items, v]);
    setDraft('');
  };
  const remove = (e) => onChange(items.filter(x => x !== e));
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
        <Users className="w-3.5 h-3.5" /> {label}
      </label>
      <div className="flex gap-1 mb-1.5">
        <Input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="text-sm"
          data-testid={`${testId}-input`}
        />
        <Button size="sm" type="button" onClick={add} variant="outline" data-testid={`${testId}-add`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {items.length === 0 && <span className="text-xs text-muted-foreground italic">Sin destinatarios.</span>}
        {items.map(e => (
          <Badge key={e} variant="secondary" className="gap-1 pr-1" data-testid={`${testId}-chip`}>
            {e}
            <button type="button" onClick={() => remove(e)} className="hover:bg-red-200 rounded-full p-0.5">
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === 'ok') {
    return <Badge className="bg-emerald-600 hover:bg-emerald-700 gap-1"><CheckCircle2 className="w-3 h-3" />OK</Badge>;
  }
  if (status === 'fail') {
    return <Badge variant="destructive" className="gap-1"><AlertCircle className="w-3 h-3" />FALLA</Badge>;
  }
  return <Badge variant="outline" className="gap-1">Sin datos</Badge>;
}

export default function HealthCheckPage() {
  const [cfg, setCfg] = useState(null);
  const [state, setState] = useState(null);
  const [history, setHistory] = useState([]);
  const [schedulers, setSchedulers] = useState([]);
  const [liveStatus, setLiveStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [forcing, setForcing] = useState(null); // key del cron en ejecución

  const [loadError, setLoadError] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [c, s, h, sch] = await Promise.all([
        api.get('/health/config'),
        api.get('/health/state'),
        api.get('/health/log?limit=20'),
        api.get('/health/schedulers'),
      ]);
      setCfg(c.data);
      setState(s.data);
      setHistory(h.data?.items || []);
      setSchedulers(sch.data?.items || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setLoadError({
          type: 'not_deployed',
          message: 'El backend de Health Check no está desplegado en este servidor. Ve al panel de Deployments y haz Deploy de nuevo para subir los endpoints faltantes.',
        });
      } else {
        setLoadError({
          type: 'generic',
          message: err?.response?.data?.detail || err?.message || 'Error cargando configuración',
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/health/config', cfg);
      setCfg(res.data);
      toast.success(res.data.enabled
        ? `Health Check activo cada ${res.data.interval_minutes} min`
        : 'Configuración guardada (alertas desactivadas)');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!cfg.to || cfg.to.length === 0) {
      toast.error('Agrega al menos un destinatario antes de enviar prueba');
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/health/test');
      if (res.data.sent) {
        toast.success(`Email de prueba enviado a ${cfg.to.length} destinatario(s)`);
      } else {
        toast.error(`No se pudo enviar: ${res.data.error || 'error desconocido'}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error enviando prueba');
    } finally {
      setSending(false);
    }
  };

  const handlePingNow = async () => {
    setPinging(true);
    try {
      const res = await api.get('/health/db');
      setLiveStatus({ ok: true, latency_ms: res.data.latency_ms, error: null, ts: new Date().toISOString() });
      toast.success(`MongoDB responde en ${res.data.latency_ms} ms`);
      // Recargar log
      api.get('/health/log?limit=20').then(r => setHistory(r.data?.items || []));
      api.get('/health/state').then(r => setState(r.data));
      api.get('/health/schedulers').then(r => setSchedulers(r.data?.items || []));
    } catch (err) {
      const detail = err?.response?.data?.detail || {};
      setLiveStatus({ ok: false, latency_ms: detail.latency_ms, error: detail.error || 'caído', ts: new Date().toISOString() });
      toast.error('MongoDB no responde');
    } finally {
      setPinging(false);
    }
  };

  const handleForceRun = async (key, label) => {
    if (!window.confirm(`Disparar manualmente: ${label}?\n\nEsto puede tardar 30-60s para DTE/Alegra.`)) return;
    setForcing(key);
    try {
      const res = await api.post(`/health/schedulers/${key}/force-run`, {}, { timeout: 180000 });
      const d = res.data;
      if (d.imported != null || d.updated != null) {
        toast.success(`${label} OK: ${d.emails_processed || 0} mails, ${d.imported || 0} importados, ${d.updated || 0} actualizados`);
      } else {
        toast.success(d.message || `${label} disparado`);
      }
      // Recargar schedulers
      api.get('/health/schedulers').then(r => setSchedulers(r.data?.items || []));
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Error disparando ${label}`);
    } finally {
      setForcing(null);
    }
  };

  if (loading || !cfg) {
    if (loadError) {
      return (
        <div className="p-4 md:p-6 max-w-3xl mx-auto" data-testid="health-check-error">
          <Card className={loadError.type === 'not_deployed' ? 'border-amber-500' : 'border-red-500'}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertCircle className={`w-5 h-5 ${loadError.type === 'not_deployed' ? 'text-amber-600' : 'text-red-600'}`} />
                {loadError.type === 'not_deployed' ? 'Backend desactualizado' : 'Error cargando Health Check'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm">{loadError.message}</p>
              {loadError.type === 'not_deployed' && (
                <div className="bg-amber-50 border-l-4 border-amber-500 p-3 text-sm rounded">
                  <strong>Solución:</strong> Ve al panel de Emergent → <strong>Manage Deployments</strong> → clic en <strong>Deploy</strong> nuevamente.
                  La build anterior se hizo antes de que terminaran de subir los nuevos archivos.
                </div>
              )}
              <Button variant="outline" size="sm" onClick={loadAll} data-testid="btn-retry-health">
                <RefreshCw className="w-4 h-4 mr-1" /> Reintentar
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    return <div className="p-6 space-y-4"><Skeleton className="h-32 w-full" /><Skeleton className="h-48 w-full" /></div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="health-check-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <HeartPulse className="w-6 h-6 text-rose-600" /> Health Check ERP
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Monitorea la conectividad a MongoDB y te avisa por email cuando el ERP se cae o se recupera.
        </p>
      </div>

      {/* Estado actual */}
      <Card data-testid="card-health-state">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="w-5 h-5 text-blue-600" /> Estado actual
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Último estado</div>
              <StatusBadge status={state?.last_status} />
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Último chequeo</div>
              <div className="font-medium">{fmtDateTime(state?.last_check)}</div>
              {state?.last_latency_ms != null && (
                <div className="text-xs text-muted-foreground">{state.last_latency_ms} ms</div>
              )}
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Último cambio de estado</div>
              <div className="font-medium">{fmtDateTime(state?.last_change)}</div>
            </div>
          </div>
          {state?.last_error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-3 text-xs font-mono text-red-800 rounded">
              <strong>Último error:</strong> {state.last_error}
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={handlePingNow} disabled={pinging} data-testid="btn-ping-now">
              <RefreshCw className={`w-4 h-4 mr-1 ${pinging ? 'animate-spin' : ''}`} /> Chequear ahora
            </Button>
            {liveStatus && (
              <span className={`text-xs px-2 py-1 rounded ${liveStatus.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                {liveStatus.ok ? `✓ ${liveStatus.latency_ms} ms` : `✗ ${liveStatus.error || 'caído'}`}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Configuración */}
      <Card data-testid="card-health-config">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Mail className="w-5 h-5" /> Configuración de alertas
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Solo se envía email cuando el estado <strong>cambia</strong> (caída o recuperación). Si sigue caído, recordatorio cada 60 min.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{cfg.enabled ? 'Activo' : 'Pausado'}</span>
              <Switch
                checked={cfg.enabled}
                onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
                data-testid="switch-health-enabled"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> Frecuencia de chequeo
              </label>
              <select
                value={cfg.interval_minutes}
                onChange={(e) => setCfg({ ...cfg, interval_minutes: Number(e.target.value) })}
                className="w-full text-sm border rounded px-2 py-1.5"
                data-testid="select-interval"
              >
                {INTERVALOS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch
                  checked={cfg.alert_on_recovery}
                  onCheckedChange={(v) => setCfg({ ...cfg, alert_on_recovery: v })}
                  data-testid="switch-alert-recovery"
                />
                <span>Avisar también cuando se <strong>recupera</strong> (🟢)</span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EmailListInput
              label="Para (To)"
              items={cfg.to || []}
              onChange={(v) => setCfg({ ...cfg, to: v })}
              placeholder="ej: c.chirino@chirinosteel.com"
              testId="health-to"
            />
            <EmailListInput
              label="Con copia (CC)"
              items={cfg.cc || []}
              onChange={(v) => setCfg({ ...cfg, cc: v })}
              placeholder="opcional"
              testId="health-cc"
            />
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t">
            <Button onClick={handleSave} disabled={saving} data-testid="btn-save-health">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Guardando...' : 'Guardar'}
            </Button>
            <Button variant="outline" onClick={handleTest} disabled={sending || !cfg.to?.length} data-testid="btn-test-health">
              <Send className="w-4 h-4 mr-1" /> {sending ? 'Enviando...' : 'Enviar email de prueba'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Crons vigilados */}
      <Card data-testid="card-health-schedulers">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="w-5 h-5 text-amber-600" /> Crons vigilados
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Si alguno lleva más del tiempo esperado sin correr, recibirás un email aunque MongoDB esté OK.
          </p>
        </CardHeader>
        <CardContent>
          {schedulers.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Sin datos.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr className="text-left">
                    <th className="p-2">Cron</th>
                    <th className="p-2">Estado</th>
                    <th className="p-2">Última corrida</th>
                    <th className="p-2 text-right">Hace</th>
                    <th className="p-2 text-right">Esperado</th>
                    <th className="p-2 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {schedulers.map((s) => (
                    <tr key={s.key} className="border-t" data-testid={`scheduler-row-${s.key}`}>
                      <td className="p-2 font-medium">{s.label}</td>
                      <td className="p-2">
                        {s.stale ? (
                          <Badge variant="destructive" className="gap-1"><AlertCircle className="w-3 h-3" />STALE</Badge>
                        ) : (
                          <Badge className="bg-emerald-600 hover:bg-emerald-700 gap-1"><CheckCircle2 className="w-3 h-3" />OK</Badge>
                        )}
                      </td>
                      <td className="p-2 text-xs">{fmtDateTime(s.last_run)}</td>
                      <td className="p-2 text-right text-xs">
                        {s.hours_since != null ? `${s.hours_since}h` : '—'}
                      </td>
                      <td className="p-2 text-right text-xs text-muted-foreground">cada {s.expected_hours}h</td>
                      <td className="p-2 text-center">
                        <Button
                          size="sm"
                          variant={s.stale ? "default" : "outline"}
                          onClick={() => handleForceRun(s.key, s.label)}
                          disabled={forcing === s.key}
                          data-testid={`force-${s.key}`}
                          title="Disparar este cron ahora"
                        >
                          <Play className={`w-3 h-3 mr-1 ${forcing === s.key ? 'animate-pulse' : ''}`} />
                          {forcing === s.key ? 'Corriendo...' : 'Forzar'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Endpoint público */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="w-5 h-5 text-blue-600" /> Endpoint público de monitoreo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm mb-2">
            Puedes apuntar UptimeRobot, Pingdom o cualquier monitor externo a este endpoint para recibir alertas adicionales por SMS / Telegram:
          </p>
          <code className="block bg-slate-100 px-3 py-2 rounded text-xs font-mono break-all">
            GET https://app.chirinosteel.com/api/health/db
          </code>
          <p className="text-xs text-muted-foreground mt-2">
            Devuelve <code>200 OK</code> si Mongo responde, <code>503 Service Unavailable</code> si falla.
          </p>
        </CardContent>
      </Card>

      {/* Historial */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="w-5 h-5" /> Historial de chequeos (últimos 20)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Aún no hay chequeos registrados.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100">
                  <tr className="text-left">
                    <th className="p-2">Timestamp</th>
                    <th className="p-2">Estado</th>
                    <th className="p-2 text-right">Latencia</th>
                    <th className="p-2">Manual</th>
                    <th className="p-2">Email</th>
                    <th className="p-2">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="p-2 font-mono">{fmtDateTime(h.timestamp)}</td>
                      <td className="p-2"><StatusBadge status={h.status} /></td>
                      <td className="p-2 text-right">{h.latency_ms ?? '—'} ms</td>
                      <td className="p-2">{h.manual ? '✓' : ''}</td>
                      <td className="p-2">
                        {h.alert_sent ? <Badge variant="outline" className="bg-blue-50">enviado</Badge> :
                         h.alert_reason && h.alert_reason !== 'no_change' ? <span className="text-muted-foreground">{h.alert_reason}</span> : ''}
                      </td>
                      <td className="p-2 text-red-600 truncate max-w-xs" title={h.error}>{h.error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
